// src/features/order/scripts/OrderScriptService.tsx
import {
  executeScriptStack,
  StackScript,
  EnvVariables,
} from '../../../../shared/api/ScriptInterpreterApi';
import type { SaleInputPayload } from './OrderScript';
import { getStockHistoryApi } from '../../stock/api/stockApi';

export type { SaleInputPayload };

/**
 * Build the env + stack for an order.
 */
function buildOrderScript(input: SaleInputPayload): {
  env: EnvVariables;
  stack: StackScript[];
} {
  const env: EnvVariables = {
    total_items:       input.items.length,
    total_amount:      input.cash_flow.total_amount,
    id_register:       input.cash_flow.id_register,
    shift_id:          input.cash_flow.shift_id,
    payment_method_id: input.cash_flow.payment_method_id,
  };
  console.log('[OrderService] envVariables:', env);

  const stack: StackScript[] = [
    {
      service:  'service_cash_flow',
      endpoint: '/record_payment',
      payload:  {
        id_register:       input.cash_flow.id_register,
        shift_id:          input.cash_flow.shift_id,
        payment_method_id: input.cash_flow.payment_method_id,
        amount:            input.cash_flow.total_amount,
      },
    },
    {
      service:  'service_purchase_sales',
      endpoint: '/sale',
      payload:  {
        items: input.items.map(i => ({
          id_product:             i.id_product,
          quantity:               i.quantity,
          product_price_purchase: i.product_price_purchase,
          product_price_sales:    i.product_price_sales,
        })),
      },
    },
    {
      service:  'service_stock',
      endpoint: '/record_stock_movement',
      payload:  {
        items: input.items.map(i => ({
          id_product:  i.id_product,
          qty:         -i.quantity,
          id_location: 0,
        })),
      },
    },
  ];
  console.log('[OrderService] stackScripts:', JSON.stringify(stack, null, 2));

  return { env, stack };
}

/**
 * Compute the current on‑hand stock for each product involved in the order.
 * Returns a map: { [productId]: availableQty }
 */
async function fetchCurrentStock(productIds: number[]): Promise<Record<number, number>> {
  if (productIds.length === 0) return {};

  const { history } = await getStockHistoryApi({ id_products: productIds });

  // Sum signed quantities → net stock level per product
  const stockMap: Record<number, number> = {};
  for (const rec of history) {
    const pid = rec.product_id;
    stockMap[pid] = (stockMap[pid] ?? 0) + (rec.level as number);
  }

  return stockMap;
}

/**
 * Executes the order stack *only if* there is enough stock.
 */
export async function executeOrderScript(
  input: SaleInputPayload
): Promise<string> {
  // 1) Check stock availability
  const productIds = input.items.map(i => i.id_product);

  try {
    const currentStock = await fetchCurrentStock(productIds);

    // Build a list of items that would go negative
    const shortages = input.items.filter(i => {
      const available = currentStock[i.id_product] ?? 0;
      return available < i.quantity;
    });

    if (shortages.length > 0) {
      console.warn('[OrderService] insufficient stock for:', shortages);
      throw new Error(
        `Insufficient stock: ${shortages
          .map(s => `product ${s.id_product} (need ${s.quantity}, have ${currentStock[s.id_product] ?? 0})`)
          .join(', ')}`
      );
    }

    // 2) Build env + stack (only after stock passes)
    const { env, stack } = buildOrderScript(input);

    console.log('[OrderService] executing script stack…');
    const result = await executeScriptStack(env, stack, 0, 'order');
    console.log('[OrderService] execution result:', result);
    return result;
  } catch (err) {
    console.error('[OrderService] execution aborted:', err);
    throw err;
  }
}
